import {
    Box,
    Button,
    Flex,
    Input,
    Text,
    Link,
  } from "@chakra-ui/react";
  import { Modal, Select } from "antd";
  import React, { useEffect, useState } from "react";
  import { MdAddLink, MdDeleteOutline } from "react-icons/md";
  import { useToastWrapper } from "../../Wrapper/toastWrapper";
  import { AdminAPI } from "../../Apis/adminAPI";
  import { errorHandler } from "../../Utils/handler";
  import { ASSET_TYPES } from "../../Constants/data"; // Claude: asset type options 14-04-2026
  
  type Props = {
    sellerId?: string;  // Claude: made optional — either sellerId or buyerId required 14-04-2026
    buyerId?: string;   // Claude: buyer digital assets support 14-04-2026
    userPlan?: any;
  };

  type DigitalAsset = {
    _id: string;
    assetTitle: string;
    assetUrl: string;
    sellerId?: string;
    buyerId?: string;
    assetType?: string; // Claude: replaced assetSummary with assetType 14-04-2026
    coverImage?: string; // Claude: optional cover image URL for buyer feed 05-05-2026
  };

  type LinkItemProps = {
    id: string;
    title: string;
    url: string;
    assetType?: string; // Claude: replaced assetSummary with assetType 14-04-2026
    onDelete: (id: string) => void;
  };
  
  // Claude: supports both seller and buyer digital assets 14-04-2026
  const AdminDigitalAssets = ({ sellerId, buyerId, userPlan }: Props) => {
    const isBuyer = !!buyerId;
    const entityId = buyerId || sellerId || "";

    const [digitalAssets, setDigitalAssets] = useState<DigitalAsset[]>([]);
    const [assetTitle, setAssetTitle] = useState("");
    const [assetUrl, setAssetUrl] = useState("");
    const [assetType, setAssetType] = useState("");
    const [coverImage, setCoverImage] = useState(""); // Claude: optional cover image URL 05-05-2026
    const [assetDeleteId, setAssetDeleteId] = useState("");
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    const { error, success } = useToastWrapper();
  
    // Modal handlers for delete confirmation
    const handleOpenDeleteConfirm = () => setDeleteConfirm(true);
    const handleCloseDeleteConfirm = () => {
      setAssetDeleteId("");
      setDeleteConfirm(false);
    };
  
    // Component to render individual digital asset link
    // Claude: replaced assetSummary with assetType badge 14-04-2026
  const LinkItem: React.FC<LinkItemProps> = ({ id, title, url, assetType, onDelete }) => {
      return (
        <Box
          key={id}
          width={"100%"}
          pt={2} pb={2} pl={2} pr={2} mb={1}
          display={"flex"} flexDirection={"row"} justifyContent={"space-between"}
          border={"1px"} borderColor={"gray.200"} borderRadius={"4px"}
        >
          <Box flex={1} mr={2}>
            {assetType && (
              <Text fontSize="10px" fontWeight={500} px={2} py="1px" borderRadius="full"
                bg="#E8F5E9" color="#2E7D32" display="inline-block" mb={1}>
                {assetType}
              </Text>
            )}
            <Link href={url} textColor={"blue.500"} isExternal wordBreak="break-word" display="block">
              {title}
            </Link>
          </Box>
          <Box cursor={"pointer"} onClick={() => onDelete(id)} flexShrink={0}>
            <MdDeleteOutline size={22} color="red" />
          </Box>
        </Box>
      );
    };
  
    // Claude: fetch digital assets for seller or buyer 14-04-2026
    const getDigitalAssets = async () => {
      if (!entityId) return;
      setLoading(true);
      try {
        const res = isBuyer
          ? await AdminAPI.getBuyerAssets(entityId)
          : await AdminAPI.getSellerAssets(entityId);
        if (res && res.data) {
          setDigitalAssets(res.data.data || []);
        }
      } catch (err) {
        console.log(err);
        error(errorHandler(err));
      } finally {
        setLoading(false);
      }
    };
  
    // Add new digital asset
    const handleAddAsset = async () => {
      // Validation
      if (!assetTitle.trim()) {
        error("Please enter asset title");
        return;
      }
      if (!assetUrl.trim()) {
        error("Please enter asset URL");
        return;
      }
  
      // URL validation
      const urlRegex = new RegExp(
        /^(https?:\/\/(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?)$/
      );
      if (!urlRegex.test(assetUrl)) {
        error("Invalid URL format");
        return;
      }
  
      // Claude: only enforce limit for sellers (buyers have no digitalAssetsLimit) 14-04-2026
      if (!isBuyer && userPlan?.digitalAssetsLimit != null && userPlan.digitalAssetsLimit !== -1 && digitalAssets.length >= userPlan.digitalAssetsLimit) { // Claude: -1 = unlimited 21-04-2026
        error("Max number of digital assets that can be created is " + userPlan.digitalAssetsLimit);
        return;
      }
  
      setLoading(true);
      try {
        // Claude: branch on isBuyer for add 14-04-2026
        const assetData = {
          ...(isBuyer ? { buyerId: entityId } : { sellerId: entityId }),
          assetTitle,
          assetUrl,
          ...(assetType ? { assetType } : {}),
          ...(coverImage.trim() ? { coverImage: coverImage.trim() } : {}),
        };

        const res = isBuyer
          ? await AdminAPI.addBuyerAsset(assetData)
          : await AdminAPI.addSellerAsset(assetData);
        if (res) {
          success("Asset added successfully");
          setAssetTitle("");
          setAssetUrl("");
          setAssetType("");
          setCoverImage("");
         
          getDigitalAssets();
        }
      } catch (err) {
        console.log(err);
        error(errorHandler(err));
      } finally {
        setLoading(false);
      }
    };
  
    // Claude: delete digital asset — seller or buyer 14-04-2026
    const deleteDigitalAsset = async (assetId: string) => {
      setLoading(true);
      try {
        const res = isBuyer
          ? await AdminAPI.deleteBuyerAsset(assetId)
          : await AdminAPI.deleteSellerAsset(assetId);
        if (res) {
          success("Asset deleted successfully");
         
          getDigitalAssets();
        }
      } catch (err) {
        console.log(err);
        error(errorHandler(err));
      } finally {
        setLoading(false);
      }
    };
  
    // Handle delete confirmation
    const handleDeleteAsset = (assetId: string) => {
      setAssetDeleteId(assetId);
      handleOpenDeleteConfirm();
    };
  
    // Claude: load on mount or entityId change 14-04-2026
    useEffect(() => {
      if (entityId) {
        getDigitalAssets();
      }
    }, [entityId]);
  
    return (
      <Box>
        {/* Delete confirmation modal  */}
        <Modal
          closeIcon={null}
          footer={null}
          title={null}
          open={deleteConfirm}
          onOk={handleCloseDeleteConfirm}
          onCancel={handleCloseDeleteConfirm}
        >
          <Box p={4}>
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              Delete Asset
            </Text>
            <Text mb={6}>
              Are you sure you want to delete this digital asset? This action cannot be undone.
            </Text>
            <Flex justifyContent="space-between">
              <Button
                onClick={handleCloseDeleteConfirm}
                variant="outline"
                borderColor="gray.300"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  deleteDigitalAsset(assetDeleteId);
                  handleCloseDeleteConfirm();
                }}
                bg="red.500"
                color="white"
                _hover={{ bg: "red.600" }}
              >
                Yes, Delete
              </Button>
            </Flex>
          </Box>
        </Modal>
  
        {/* Digital Assets Section Title */}
        <Text fontSize="xs" fontWeight="500" mb={2}>
          Catalog and Digital Assets
        </Text>
  
        {/* Claude: add inputs — Type | Title | Cover Image | URL + submit 14-04-2026; reordered 05-05-2026 */}
        <Flex gap={2} mb={2} alignItems="center">
          <Box flex={1}>
            <Select
              placeholder="Type"
              style={{ width: "100%", height: "28px" }}
              value={assetType || undefined}
              onChange={(val) => setAssetType(val)}
              options={ASSET_TYPES.map((t) => ({ label: t, value: t }))}
              allowClear
            />
          </Box>
          <Box flex={2}>
            <Input
              height="28px" fontSize="xs"
              placeholder="Title"
              value={assetTitle}
              onChange={(e) => setAssetTitle(e.target.value)}
            />
          </Box>
          <Box flex={2}>
            <Input
              height="28px" fontSize="xs"
              placeholder="Cover Image URL (optional)"
              value={coverImage}
              onChange={(e) => setCoverImage(e.target.value)}
            />
          </Box>
        </Flex>
        <Flex gap={2} mb={4} alignItems="center">
          <Box flex={1}>
            <Input
              height="28px" fontSize="xs"
              placeholder="URL"
              value={assetUrl}
              onChange={(e) => setAssetUrl(e.target.value)}
            />
          </Box>
          <Box>
            <Button
              height="28px" fontSize="xs" px={3}
              bg="green.500" color="white" _hover={{ bg: "green.600" }}
              onClick={handleAddAsset} isLoading={loading} disabled={loading}
            >
              <MdAddLink size={20} />
            </Button>
          </Box>
        </Flex>
  
        {/* Display Digital Assets */}
        <Box>
          {digitalAssets && digitalAssets.length > 0 ? (
            digitalAssets.map((asset) => (
              <LinkItem
                key={asset._id}
                id={asset._id}
                title={asset.assetTitle}
                url={asset.assetUrl}
                assetType={asset.assetType}
                onDelete={handleDeleteAsset}
              />
            ))
          ) : (
            <Text fontSize="sm" color="gray.500" fontStyle="italic">
              No digital assets added
            </Text>
          )}
        </Box>
      </Box>
    );
  };
  
  export default AdminDigitalAssets;